/**
 * Full-Width Fixed-Text Slider — vanilla JS, no external libraries.
 * Images slide behind a fixed text overlay. Handles: infinite loop,
 * autoplay w/ hover-pause, drag/swipe momentum, keyboard nav, pagination
 * dots, parallax, reduced-motion, and Shopify Theme Editor block select.
 */
(function () {
  'use strict';

  var reduceMotionMQ = window.matchMedia('(prefers-reduced-motion: reduce)');

  function prefersReducedMotion() {
    return reduceMotionMQ.matches;
  }

  function initFixedOverlaySlider(section) {
    if (!section || section.dataset.ftsInitialized === 'true') return;

    var carousel = section.querySelector('[data-fts-carousel]');
    var track = section.querySelector('[data-fts-track]');
    if (!carousel || !track) return;

    var realSlides = Array.prototype.slice.call(track.querySelectorAll('[data-fts-slide]'));
    var slideCount = realSlides.length;
    if (slideCount === 0) return;

    section.dataset.ftsInitialized = 'true';

    var dotsWrap = section.querySelector('[data-fts-dots]');

    var loopEnabled = slideCount > 1;
    var autoplayEnabled = section.dataset.autoplay === 'true' && slideCount > 1;
    var autoplaySpeed = parseInt(section.dataset.autoplaySpeed, 10) || 5000;
    var parallaxEnabled = section.dataset.parallax === 'true';

    // ---- Build infinite-loop clones ----
    if (loopEnabled) {
      var headClone = realSlides[0].cloneNode(true);
      var tailClone = realSlides[slideCount - 1].cloneNode(true);
      [headClone, tailClone].forEach(function (clone) {
        clone.setAttribute('data-clone', 'true');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button').forEach(function (el) {
          el.setAttribute('tabindex', '-1');
        });
      });
      track.insertBefore(tailClone, realSlides[0]);
      track.appendChild(headClone);
    }

    var allSlides = Array.prototype.slice.call(track.querySelectorAll('[data-fts-slide]'));
    var baseOffset = loopEnabled ? 1 : 0;

    // ---- Pagination dots ----
    var dots = [];
    if (dotsWrap && loopEnabled) {
      realSlides.forEach(function (_, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'fts-dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.addEventListener('click', function () {
          goToSlide(i + baseOffset, { smooth: true });
        });
        dotsWrap.appendChild(dot);
        dots.push(dot);
      });
    }

    // ---- Geometry helpers ----
    function targetScrollLeftFor(slideEl) {
      var trackRect = track.getBoundingClientRect();
      var slideRect = slideEl.getBoundingClientRect();
      return (
        track.scrollLeft +
        (slideRect.left - trackRect.left) -
        (trackRect.width - slideRect.width) / 2
      );
    }

    function closestSlideIndex() {
      var trackRect = track.getBoundingClientRect();
      var center = trackRect.left + trackRect.width / 2;
      var closestIdx = 0;
      var closestDist = Infinity;
      allSlides.forEach(function (slide, i) {
        var r = slide.getBoundingClientRect();
        var slideCenter = r.left + r.width / 2;
        var dist = Math.abs(slideCenter - center);
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = i;
        }
      });
      return closestIdx;
    }

    var isJumping = false;

    function goToSlide(domIndex, opts) {
      opts = opts || {};
      var clamped = Math.max(0, Math.min(allSlides.length - 1, domIndex));
      var slideEl = allSlides[clamped];
      if (!slideEl) return;
      var left = targetScrollLeftFor(slideEl);
      var instant = opts.instant || prefersReducedMotion();
      if (instant) {
        isJumping = true;
        track.setAttribute('data-jumping', 'true');
        track.scrollLeft = left;
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            track.removeAttribute('data-jumping');
            isJumping = false;
          });
        });
      } else {
        track.scrollTo({ left: left, behavior: 'smooth' });
      }
    }

    function next() {
      goToSlide(closestSlideIndex() + 1, { smooth: true });
    }

    function prev() {
      goToSlide(closestSlideIndex() - 1, { smooth: true });
    }

    // ---- Active slide / dot sync ----
    var updateScheduled = false;

    function updateActiveSlide() {
      updateScheduled = false;
      var activeIdx = closestSlideIndex();

      allSlides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === activeIdx);
      });

      if (dots.length) {
        var realIdx = ((activeIdx - baseOffset) + slideCount) % slideCount;
        dots.forEach(function (dot, i) {
          var isActive = i === realIdx;
          dot.classList.toggle('is-active', isActive);
          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      if (parallaxEnabled && !prefersReducedMotion()) {
        applyParallax();
      }
    }

    function scheduleUpdate() {
      if (!updateScheduled) {
        updateScheduled = true;
        requestAnimationFrame(updateActiveSlide);
      }
    }

    // ---- Parallax ----
    function applyParallax() {
      var trackRect = track.getBoundingClientRect();
      var center = trackRect.left + trackRect.width / 2;
      allSlides.forEach(function (slide) {
        var media = slide.querySelector('[data-fts-parallax-el]');
        if (!media) return;
        var r = slide.getBoundingClientRect();
        var slideCenter = r.left + r.width / 2;
        var offset = slideCenter - center;
        var factor = 0.12;
        var shift = Math.max(-80, Math.min(80, -offset * factor));
        media.style.transform = 'translateX(' + shift + 'px)';
      });
    }

    // ---- Infinite-loop wrap-around ----
    var settleTimer = null;

    function handleSettle() {
      if (!loopEnabled || isJumping) return;
      var idx = closestSlideIndex();
      if (idx === 0) {
        goToSlide(slideCount + baseOffset - 1, { instant: true });
      } else if (idx === allSlides.length - 1) {
        goToSlide(baseOffset, { instant: true });
      }
    }

    track.addEventListener('scroll', function () {
      scheduleUpdate();
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(handleSettle, 120);
    });

    // ---- Autoplay ----
    var autoplayTimer = null;

    function startAutoplay() {
      if (!autoplayEnabled || prefersReducedMotion()) return;
      stopAutoplay();
      autoplayTimer = setInterval(next, autoplaySpeed);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    //carousel.addEventListener('mouseenter', stopAutoplay);
    //carousel.addEventListener('mouseleave', startAutoplay);
    carousel.addEventListener('touchstart', stopAutoplay, { passive: true });
    carousel.addEventListener('touchend', startAutoplay, { passive: true });
    carousel.addEventListener('focusin', stopAutoplay);
    carousel.addEventListener('focusout', startAutoplay);

    if (reduceMotionMQ.addEventListener) {
      reduceMotionMQ.addEventListener('change', function () {
        if (prefersReducedMotion()) {
          stopAutoplay();
        } else {
          startAutoplay();
        }
      });
    }

    // ---- Keyboard navigation ----
    carousel.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      }
    });

    // ---- Handle Link Clicks ---- //
    function onPointerClick(e){
      if (dragState) return;

      // 1. Find the element using an attribute selector
      const element = e.target.getAttribute('data-url');
      
      // 2. Check if the element exists
      if (element) {
        // 3. Extract the link from the data-url attribute
        const targetLink = element; // element.dataset.url;

        // 4. Navigate to the link
        window.location.href = targetLink;
      }
    }

    // ---- Drag / swipe with lightweight momentum ----
    var dragState = null;
    var DRAG_THRESHOLD = 6;

    function onPointerDown(e) {
      if (e.button !== undefined && e.button !== 0 && e.pointerType === 'mouse') return;
      stopAutoplay();
      dragState = {
        startX: e.clientX,
        startScrollLeft: track.scrollLeft,
        lastX: e.clientX,
        lastT: performance.now(),
        velocity: 0,
        moved: false,
      };
      track.setPointerCapture && track.setPointerCapture(e.pointerId);
    }

    function onPointerMove(e) {
      if (!dragState) return;
      var dx = e.clientX - dragState.startX;
      if (Math.abs(dx) > DRAG_THRESHOLD) {
        dragState.moved = true;
        track.setAttribute('data-dragging', 'true');
      }
      if (dragState.moved) {
        e.preventDefault();
        track.scrollLeft = dragState.startScrollLeft - dx;

        var now = performance.now();
        var dt = now - dragState.lastT;
        if (dt > 0) {
          dragState.velocity = (e.clientX - dragState.lastX) / dt;
        }
        dragState.lastX = e.clientX;
        dragState.lastT = now;
      }
    }

    function onPointerUp() {
      if (!dragState) return;
      var wasDrag = dragState.moved;
      track.removeAttribute('data-dragging');

      if (wasDrag) {
        var velocity = dragState.velocity;
        var VELOCITY_THRESHOLD = 0.35;
        var currentIdx = closestSlideIndex();

        if (velocity < -VELOCITY_THRESHOLD) {
          goToSlide(currentIdx + 1, { smooth: true });
        } else if (velocity > VELOCITY_THRESHOLD) {
          goToSlide(currentIdx - 1, { smooth: true });
        } else {
          goToSlide(currentIdx, { smooth: true });
        }
      }

      dragState = null;
      startAutoplay();
    }

    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', onPointerUp);
    track.addEventListener('pointercancel', onPointerUp);
    track.addEventListener('click', onPointerClick);

    track.querySelectorAll('img').forEach(function (img) {
      img.addEventListener('dragstart', function (e) {
        e.preventDefault();
      });
    });

    // ---- Resize handling ----
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        goToSlide(closestSlideIndex(), { instant: true });
      }, 150);
    });

    // ---- Shopify Theme Editor integration ----
    section.addEventListener('shopify:block:select', function (e) {
      var blockEl = e.target.closest('[data-fts-slide]');
      if (!blockEl) return;
      stopAutoplay();
      var idx = allSlides.indexOf(blockEl);
      if (idx !== -1) goToSlide(idx, { instant: true });
    });

    section.addEventListener('shopify:block:deselect', function () {
      startAutoplay();
    });

    // ---- Init ----
    goToSlide(baseOffset, { instant: true });
    updateActiveSlide();
    if (parallaxEnabled && !prefersReducedMotion()) applyParallax();
    startAutoplay();
  }

  function initAll() {
    document.querySelectorAll('[data-fts-section]').forEach(function (section) {
      initFixedOverlaySlider(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('[data-fts-section]');
    if (section) initFixedOverlaySlider(section);
  });
})();